import { SupplyComponent, SupplyFeeder, COMPONENT_KINDS, FEEDER_KINDS, FEEDER_TYPES } from '../types/SimpleSupplyPoint';
import { generateUniqueId } from './generateUniqueId';

/**
 * Legacy SupplyPoint'i Simple SupplyPoint'e dönüştüren migration utility
 */
export class LegacyMigration {

  /**
   * Ana migration fonksiyonu
   * @param {object} legacySupply - Legacy supply point data
   * @returns {object} Simple supply point data
   */
  static migrateToSimpleSupplyPoint(legacySupply) {
    
    

    const components = [];
    const feeders = [];
    const migrationLog = [];

    try {
      // 1. Housing'leri component'lere çevir
      this.migrateHousings(legacySupply.housings || [], components, migrationLog);

      // 2. Connection'ları feeder'lara çevir
      this.migrateConnections(legacySupply.connections || [], feeders, migrationLog);

      // 3. Branch circuit'leri ekle
      this.migrateBranchCircuits(legacySupply.branchCircuits || [], components, feeders, migrationLog);

      // 4. Load'ları ekle
      this.migrateLoads(legacySupply.loads || [], components, feeders, migrationLog);

      // 5. Relationship'leri düzelt
      this.fixRelationships(components, feeders, migrationLog);

      // 6. Orphaned component'leri temizle
      this.cleanupOrphanedComponents(components, feeders, migrationLog);

      // 7. Validation
      const errors = this.validateMigration(components, feeders);

      const result = {
        feeders,
        components,
        metadata: {
          migratedFrom: "legacy",
          migratedAt: new Date().toISOString(),
          migrationLog,
          migrationErrors: errors,
          originalCounts: {
            housings: legacySupply.housings?.length || 0,
            connections: legacySupply.connections?.length || 0,
            loads: legacySupply.loads?.length || 0,
            branchCircuits: legacySupply.branchCircuits?.length || 0
          },
          resultCounts: {
            components: components.length,
            feeders: feeders.length
          }
        }
      };

      
      

      return result;

    } catch (error) {
      console.error("❌ LEGACY MIGRATION FAILED:", error);
      throw new Error(`Migration failed: ${error.message}`);
    }
  }

  /**
   * Housing'leri component'lere çevir
   */
  static migrateHousings(housings, components, migrationLog) {
    

    housings.forEach(housing => {
      try {
        const componentKind = this.determineComponentKind(housing);

        const component = new SupplyComponent({
          id: housing.id,
          kind: componentKind,
          name: housing.name || `Housing ${housing.id}`,
          electrical: housing.electrical || {},
          incomingFeeders: [], // Sonra doldurulacak
          outgoingFeeders: [], // Sonra doldurulacak
          metadata: {
            originalType: housing.type,
            feederType: housing.feederType,
            components: housing.components,
            enteries: housing.enteries,
            outs: housing.outs
          }
        });

        components.push(component);
        migrationLog.push(`Housing migrated: ${housing.name} (${housing.type} → ${componentKind})`);

      } catch (error) {
        migrationLog.push(`Housing migration failed: ${housing.id} - ${error.message}`);
      }
    });

    
  }

  /**
   * Connection'ları feeder'lara çevir
   */
  static migrateConnections(connections, feeders, migrationLog) {
    

    connections.forEach(connection => {
      try {
        // Sadece feeder type'ı olan connection'ları çevir
        if (!connection.feederType || !connection.fromHousing || !connection.toHousing) {
          migrationLog.push(`Connection skipped: ${connection.id} - missing feeder data`);
          return;
        }

        const feederKind = this.determineFeederKind(connection);

        const feeder = new SupplyFeeder({
          id: connection.id,
          from: connection.fromHousing,
          to: connection.toHousing,
          type: connection.feederType,
          kind: feederKind,
          power: {
            installedPower: 0, // Hesaplanacak
            demandPower: 0,
            current: 0
          },
          phaseCount: 3, // Default
          cable: connection.cable || this.getDefaultCable(),
          protection: this.extractProtection(connection),
          metadata: {
            originalType: connection.type,
            masterNodeId: connection.masterNodeId
          }
        });

        feeders.push(feeder);
        migrationLog.push(`Connection migrated: ${connection.fromHousing} → ${connection.toHousing} (${connection.feederType})`);

      } catch (error) {
        migrationLog.push(`Connection migration failed: ${connection.id} - ${error.message}`);
      }
    });

    
  }

  /**
   * Branch circuit'leri ekle
   */
  static migrateBranchCircuits(branchCircuits, components, feeders, migrationLog) {
    

    branchCircuits.forEach(branch => {
      try {
        // Branch component
        const branchComponent = new SupplyComponent({
          id: branch.id,
          kind: COMPONENT_KINDS.BRANCH,
          name: branch.name || `Branch ${branch.id}`,
          electrical: {
            installedPower: branch.power || 0,
            demandPower: branch.power || 0,
            current: this.calculateCurrentFromPower(branch.power, branch.phaseCount || 1),
            phaseCount: branch.phaseCount || 1
          },
          incomingFeeders: [], // Housing'den gelecek
          outgoingFeeders: [],
          metadata: {
            branchType: branch.branchType,
            housing: branch.housing,
            originalBranch: true
          }
        });

        components.push(branchComponent);

        // Housing ile branch arasında feeder oluştur
        if (branch.housing?.id) {
          const feederId = generateUniqueId("feeder");

          const branchFeeder = new SupplyFeeder({
            id: feederId,
            from: branch.housing.id,
            to: branch.id,
            type: "branch",
            kind: FEEDER_KINDS.BRANCH_CIRCUIT,
            power: {
              installedPower: branch.power || 0,
              demandPower: branch.power || 0,
              current: this.calculateCurrentFromPower(branch.power, branch.phaseCount || 1)
            },
            cable: this.getDefaultBranchCable(),
            protection: {},
            metadata: {
              branchType: branch.branchType
            }
          });

          feeders.push(branchFeeder);
          branchComponent.incomingFeeders.push(feederId);
        }

        migrationLog.push(`Branch migrated: ${branch.name} (${branch.power}W)`);

      } catch (error) {
        migrationLog.push(`Branch migration failed: ${branch.id} - ${error.message}`);
      }
    });

    
  }

  /**
   * Load'ları ekle
   */
  static migrateLoads(loads, components, feeders, migrationLog) {
    

    loads.forEach(load => {
      try {
        // Load component
        const loadComponent = new SupplyComponent({
          id: load.id,
          kind: COMPONENT_KINDS.LOAD,
          name: load.name || `Load ${load.id}`,
          electrical: {
            installedPower: load.power || 0,
            demandPower: load.power || 0,
            current: this.calculateCurrentFromPower(load.power, 1),
            phaseCount: 1
          },
          incomingFeeders: [],
          outgoingFeeders: [],
          metadata: {
            loadType: load.branchType,
            from: load.from,
            originalLoad: true
          }
        });

        components.push(loadComponent);

        // Connection'dan parent bulma
        if (load.from) {
          // load.from genellikle connection ID'si
          // Bu connection'ın housing'ini bul
          const parentFeeder = feeders.find(f => f.id === load.from);

          if (parentFeeder) {
            const loadFeederId = generateUniqueId("feeder");

            const loadFeeder = new SupplyFeeder({
              id: loadFeederId,
              from: parentFeeder.to, // Connection'ın to housing'i
              to: load.id,
              type: "load",
              kind: FEEDER_KINDS.BRANCH_CIRCUIT,
              power: {
                installedPower: load.power || 0,
                demandPower: load.power || 0,
                current: this.calculateCurrentFromPower(load.power, 1)
              },
              cable: this.getDefaultLoadCable(),
              protection: {},
              metadata: {
                loadType: load.branchType
              }
            });

            feeders.push(loadFeeder);
            loadComponent.incomingFeeders.push(loadFeederId);
          }
        }

        migrationLog.push(`Load migrated: ${load.name || load.id} (${load.power}W)`);

      } catch (error) {
        migrationLog.push(`Load migration failed: ${load.id} - ${error.message}`);
      }
    });

    
  }

  /**
   * Relationship'leri düzelt
   */
  static fixRelationships(components, feeders, migrationLog) {
    

    feeders.forEach(feeder => {
      const fromComponent = components.find(c => c.id === feeder.from);
      const toComponent = components.find(c => c.id === feeder.to);

      if (fromComponent && !fromComponent.outgoingFeeders.includes(feeder.id)) {
        fromComponent.outgoingFeeders.push(feeder.id);
      }

      if (toComponent && !toComponent.incomingFeeders.includes(feeder.id)) {
        toComponent.incomingFeeders.push(feeder.id);
      }

      if (!fromComponent || !toComponent) {
        migrationLog.push(`Relationship warning: Feeder ${feeder.id} has missing component(s)`);
      }
    });

    
  }

  /**
   * Orphaned component'leri temizle
   */
  static cleanupOrphanedComponents(components, feeders, migrationLog) {
    

    const initialCount = components.length;

    // Supply olmayan ve incoming feeder'ı olmayan component'leri bul
    const orphanedComponents = components.filter(component => {
      if (component.kind === COMPONENT_KINDS.SUPPLY) return false;
      return component.incomingFeeders.length === 0;
    });

    // Orphaned component'leri sil
    orphanedComponents.forEach(orphaned => {
      const index = components.findIndex(c => c.id === orphaned.id);
      if (index !== -1) {
        components.splice(index, 1);
        migrationLog.push(`Orphaned component removed: ${orphaned.name} (${orphaned.id})`);
      }

      // İlgili feeder'ları da sil
      const relatedFeeders = feeders.filter(f => f.from === orphaned.id || f.to === orphaned.id);
      relatedFeeders.forEach(feeder => {
        const feederIndex = feeders.findIndex(f => f.id === feeder.id);
        if (feederIndex !== -1) {
          feeders.splice(feederIndex, 1);
        }
      });
    });

    
  }

  /**
   * Migration validation
   */
  static validateMigration(components, feeders) {
    const errors = [];

    // Supply component kontrolü
    const supplyComponents = components.filter(c => c.kind === COMPONENT_KINDS.SUPPLY);
    if (supplyComponents.length === 0) {
      errors.push("No supply component found after migration");
    } else if (supplyComponents.length > 1) {
      errors.push("Multiple supply components found after migration");
    }

    // Feeder relationship kontrolü
    feeders.forEach(feeder => {
      const fromExists = components.find(c => c.id === feeder.from);
      const toExists = components.find(c => c.id === feeder.to);

      if (!fromExists) {
        errors.push(`Feeder ${feeder.id}: from component ${feeder.from} not found`);
      }
      if (!toExists) {
        errors.push(`Feeder ${feeder.id}: to component ${feeder.to} not found`);
      }
    });

    // Circular dependency kontrolü
    const visited = new Set();
    const recursionStack = new Set();

    const hasCircularDependency = (componentId) => {
      if (recursionStack.has(componentId)) return true;
      if (visited.has(componentId)) return false;

      visited.add(componentId);
      recursionStack.add(componentId);

      const outgoingFeeders = feeders.filter(f => f.from === componentId);
      for (const feeder of outgoingFeeders) {
        if (hasCircularDependency(feeder.to)) return true;
      }

      recursionStack.delete(componentId);
      return false;
    };

    components.forEach(component => {
      if (hasCircularDependency(component.id)) {
        errors.push(`Circular dependency detected involving component: ${component.name}`);
      }
    });

    return errors;
  }

  // ===============================
  // HELPER METHODS
  // ===============================

  static determineComponentKind(housing) {
    if (housing.feederType === "supply") return COMPONENT_KINDS.SUPPLY;
    if (housing.type === "meterPanel") return COMPONENT_KINDS.METER_PANEL;
    if (housing.type === "distributionPanel") return COMPONENT_KINDS.DIST_PANEL;
    if (housing.feederType === "mf-03-a") return COMPONENT_KINDS.METER_PANEL;
    if (housing.feederType === "mf-03-b") return COMPONENT_KINDS.DIST_PANEL;
    return COMPONENT_KINDS.DIST_PANEL; // Default
  }

  static determineFeederKind(connection) {
    if (connection.feederType === "mf-03-a") return FEEDER_KINDS.MAIN_FEEDER;
    if (connection.feederType === "mf-03-b") return FEEDER_KINDS.SUB_FEEDER;
    return FEEDER_KINDS.BRANCH_CIRCUIT;
  }

  static extractProtection(connection) {
    // Legacy connection'dan protection bilgilerini çıkar
    // Bu implementation'ın detayları proje yapısına göre adapte edilmeli
    return {
      breaker1: null,
      breaker2: null
    };
  }

  static calculateCurrentFromPower(power, phaseCount = 3) {
    if (!power || power <= 0) return 0;
    const voltage = phaseCount === 3 ? 380 : 220;
    return power / voltage;
  }

  static getDefaultCable() {
    return {
      type: "NYY",
      material: "Cu",
      section: 10,
      length: 20,
      currentCap: 44,
      count: 1,
      housing: "pipe"
    };
  }

  static getDefaultBranchCable() {
    return {
      type: "NYM",
      material: "Cu",
      section: 2.5,
      length: 10,
      currentCap: 21,
      count: 1,
      housing: "pipe"
    };
  }

  static getDefaultLoadCable() {
    return {
      type: "NYM",
      material: "Cu",
      section: 1.5,
      length: 5,
      currentCap: 15,
      count: 1,
      housing: "pipe"
    };
  }

  /**
   * Migration preview - actual migration yapmadan önce sonucu göster
   */
  static previewMigration(legacySupply) {
    const preview = {
      input: {
        housings: legacySupply.housings?.length || 0,
        connections: legacySupply.connections?.length || 0,
        loads: legacySupply.loads?.length || 0,
        branchCircuits: legacySupply.branchCircuits?.length || 0
      },
      expectedOutput: {
        components: 0,
        feeders: 0
      },
      warnings: [],
      recommendations: []
    };

    // Expected component count
    preview.expectedOutput.components =
      (legacySupply.housings?.length || 0) +
      (legacySupply.branchCircuits?.length || 0) +
      (legacySupply.loads?.length || 0);

    // Expected feeder count
    preview.expectedOutput.feeders =
      (legacySupply.connections?.filter(c => c.feederType)?.length || 0) +
      (legacySupply.branchCircuits?.length || 0) +
      (legacySupply.loads?.length || 0);

    // Warnings
    if (!legacySupply.housings || legacySupply.housings.length === 0) {
      preview.warnings.push("No housings found - supply component will be missing");
    }

    const connectionsWithoutFeederType = legacySupply.connections?.filter(c => !c.feederType) || [];
    if (connectionsWithoutFeederType.length > 0) {
      preview.warnings.push(`${connectionsWithoutFeederType.length} connections without feederType will be skipped`);
    }

    // Recommendations
    if (preview.input.loads > 50) {
      preview.recommendations.push("Large number of loads detected - consider grouping them into branches");
    }

    if (preview.input.connections > preview.input.housings * 2) {
      preview.recommendations.push("Complex connection structure detected - validate relationships after migration");
    }

    return preview;
  }
}